import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect } from "react";
import { pick, useT } from "../i18n/index.js";
import { Panel } from "../ui/Panel.js";
import { Button } from "../ui/Button.js";
import { navigate } from "../router.js";
import { useStore } from "../store/state.js";
import { BundleList } from "../store/cards.js";
import { AssetIcon } from "../assets/Icon.js";
import { useEvents } from "./state.js";
import { growthApi } from "./api.js";
import { MissionRow } from "./DailyView.js";

function linkTarget(link: string | null): string | null {
  if (!link) return null;
  if (link.startsWith("bld.")) return "/app";
  if (link.startsWith("store:")) return `/app/store/${link.slice(6) === "featured" ? "" : link.slice(6)}`;
  const map: Record<string, string> = { base: "/app", gather: "/app/map", units: "/app/units", world: "/app/map", social: "/app/chat", research: "/app", store: "/app/store", arena: "/app/arena", expedition: "/app/expeditions", pvp: "/app/map", modules: "/app/units", hive: "/app/map", defence: "/app", collection: "/app/units", siege: "/app/events", vip: "/app/store/vip", boss: "/app/map", ruins: "/app/map", warlord: "/app/map", endgame: "/app/map" };
  return map[link] ?? null;
}

/** The growth chain: chapter strip, the current chapter's ten missions and the chapter chest (R64). */
export function GrowthView() {
  const t = useT();
  const growth = useEvents((s) => s.growth);
  const refreshGrowth = useEvents((s) => s.refreshGrowth);
  const run = useEvents((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  useEffect(() => {
    void refreshGrowth();
  }, [refreshGrowth]);
  if (!growth) return <p className="hint">{t("common.loading")}</p>;
  const claimMission = async (id: string) => {
    const res = await run(() => growthApi.claimMission(id), t("events.toast.growth"));
    if (res) {
      celebrate({ key: id, granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      void refreshGrowth();
    }
  };
  const claimChapter = async (chapter: number) => {
    const res = await run(() => growthApi.claimChapter(chapter), t("events.toast.chapter"));
    if (res) {
      celebrate({ key: `chapter-${chapter}`, granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      void refreshGrowth();
    }
  };
  const current = growth.chapters.find((c) => c.chapter === growth.chapter) ?? null;
  return (
    <Panel title={t("events.tab.growth")}>
      <div className="chapter-strip" data-testid="growth-chapters">
        {growth.chapters.map((c) => (
          <div key={c.chapter} className={`chapter ${c.chapter === growth.chapter ? "active" : ""} ${c.chapter > growth.chapter ? "locked" : ""}`} data-testid={`growth-chapter-${c.chapter}`}>
            <div className="mono">{c.chapter}</div>
            <div className="ellipsis" style={{ fontSize: 10 }}>{pick(c.name)}</div>
            {c.unit ? <AssetIcon id={c.unit} size={28} portrait style={{ margin: "2px auto 0" }} /> : null}
            {c.claimed ? <div className="dim"><FunctionalIcon name="check" /></div> : null}
          </div>
        ))}
      </div>
      {current ? (
        <div className="row" style={{ gap: 10, marginTop: 8, alignItems: "flex-start" }}>
          <div className="grow">
            <div className="offer-name">{t("events.growth.chapter", { n: current.chapter, name: pick(current.name) })}</div>
            <BundleList bundle={{ ...current.reward, ...(current.unit ? { units: [current.unit] } : {}) }} compact />
          </div>
          <Button variant="gold" disabled={!current.claimable} onClick={() => void claimChapter(current.chapter)} data-testid="growth-chapter-claim">
            {t("events.growth.chest")}
          </Button>
        </div>
      ) : (
        <p className="hint">{t("events.growth.done")}</p>
      )}
      <div className="missions" data-testid="growth-missions">
        {growth.missions.map((m) => {
          const to = linkTarget(m.link);
          return (
            <div key={m.id} className="row" style={{ gap: 8, alignItems: "stretch" }}>
              <div className="grow">
                <MissionRow m={m} testId={`growth-${m.id}`} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 4 }}>
                {m.done && !m.claimed ? (
                  <Button variant="gold" onClick={() => void claimMission(m.id)} data-testid={`growth-claim-${m.id}`}>
                    {t("common.claim")}
                  </Button>
                ) : m.claimed ? (
                  <span className="dim" style={{ textAlign: "center" }}><FunctionalIcon name="check" /></span>
                ) : to ? (
                  <Button variant="ghost" onClick={() => navigate(to)} data-testid={`growth-go-${m.id}`}>
                    {t("events.growth.go")}
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
